/* @flow */
import idx from 'idx';
import gql from 'graphql-tag';
import { graphql, compose } from 'react-apollo';
import * as React from 'react';

import Avatar from 'components/Avatar';
import MenuButton from 'components/MenuButtonDeprecated';
import MenuItem from 'components/MenuItem';
import Menu from 'components/Menu';
import AvatarEditorModalTriggerButton from 'components/AvatarEditorModalTriggerButton';
import { uploadFileRequest } from 'clients/api/fileUploads';
import type { V2File } from 'clients/api/fileUploads';
import { uploadFile } from 'lib/fileUploadHelper';
import ToastActions from 'actions/ToastActions';

import './styles/ProfilePictureUploadButton.scss';

const PLACEHOLDER_URL = require('assets/images/avatars/profile-picture-placeholder.svg');

type Props = {
  imageUrl?: string,
  updatePhoto: (file: V2File) => Promise<*>,
  removePhoto: () => Promise<*>,
};

type State = {
  thumbnailDataUrl?: string,
};

class ProfilePictureUploadButton extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { thumbnailDataUrl: undefined };
  }

  renderMenu() {
    return (
      <Menu>
        <MenuItem
          onClick={() => {
            this.props.removePhoto().then(
              () => ToastActions.showSuccessToast('Profile photo removed'),
              e => ToastActions.showAPIError(e)
            );
          }}>
          Remove current photo
        </MenuItem>
      </Menu>
    );
  }

  render() {
    const { imageUrl } = this.props;
    const { thumbnailDataUrl } = this.state;

    if (imageUrl) {
      return (
        <MenuButton className="ProfilePictureUploadButton" level="icon" menu={this.renderMenu()}>
          <Avatar className="ProfilePictureUploadButton-avatar" image={imageUrl} />
        </MenuButton>
      );
    }

    if (thumbnailDataUrl) {
      return (
        <div className="ProfilePictureUploadButton">
          <Avatar className="ProfilePictureUploadButton-thumbnail" image={thumbnailDataUrl} />
        </div>
      );
    }

    return (
      <div className="ProfilePictureUploadButton">
        <AvatarEditorModalTriggerButton
          className="ProfilePictureUploadButton-uploadButton"
          onSave={(image: File) => {
            const uploadHandler = (
              fileContextKey: string,
              filename: string,
              file: File,
              onSuccess?: (file: V2File) => void,
              onFailure?: (err: Error) => void,
              privateUpload?: boolean
            ) => {
              uploadFileRequest(file, privateUpload).then(
                response => {
                  return this.props.updatePhoto(response).then(
                    () => {
                      this.setState({ thumbnailDataUrl: undefined });
                      ToastActions.showSuccessToast('Your profile photo has been added!');
                    },
                    err => ToastActions.showAPIError(err)
                  );
                },
                err => ToastActions.showAPIError(err)
              );
            };

            const thumbnailHandler = (
              fileContextKey: string,
              filename: string,
              dataUrl: string
            ) => {
              this.setState({ thumbnailDataUrl: dataUrl });
            };

            uploadFile('', image, uploadHandler, thumbnailHandler);
          }}
          level="icon">
          <Avatar className="ProfilePictureUploadButton-avatar" image={PLACEHOLDER_URL} />
        </AvatarEditorModalTriggerButton>
      </div>
    );
  }
}

const withMutations = graphql(
  gql`
    mutation updateUserProfilePicture($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          uuid
          profile_picture {
            urls {
              cdn
            }
          }
        }
        error {
          message
        }
      }
    }
  `,
  {
    props({ mutate }) {
      return {
        updatePhoto(file: V2File) {
          const input = { profile_picture_id: file.id };
          return mutate({ variables: { input } });
        },
        removePhoto() {
          const input = { profile_picture_id: null };
          return mutate({ variables: { input } });
        },
      };
    },
  }
);

const withPicture = graphql(
  gql`
    query ProfilePictureUploadButtonQuery {
      user: me {
        uuid
        profile_picture {
          urls {
            cdn
          }
        }
      }
    }
  `,
  {
    props: ({ data }) => {
      const imageUrl = idx(data, _ => _.user.profile_picture.urls.cdn);
      if (!imageUrl) {
        return {};
      }

      return { imageUrl };
    },
  }
);

export { ProfilePictureUploadButton };
export default compose(withMutations, withPicture)(ProfilePictureUploadButton);
